import React, { useState } from 'react';
import { Modal, Stack, Text, Select, Textarea, Button, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { OTPReasonCode } from '../../../../global/entities/OTP/OTP';

export type OutcomeAction = 'complete' | 'abort' | 'append';

export interface IOutcomeDialogResult {
    action: OutcomeAction;
    reasonCode: OTPReasonCode;
    reasonText?: string;
}

interface IOTPOutcomeDialogProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: (result: IOutcomeDialogResult) => void;
    action: OutcomeAction;
    title?: string;
}

const reasonCodeOptions = [
    { value: String(OTPReasonCode.Other), labelKey: 'otp.reasonCodes.other' },
    { value: String(OTPReasonCode.PainFlare), labelKey: 'otp.reasonCodes.painFlare' },
    { value: String(OTPReasonCode.AdverseEffects), labelKey: 'otp.reasonCodes.adverseEffects' },
    { value: String(OTPReasonCode.PatientPreference), labelKey: 'otp.reasonCodes.patientPreference' },
    { value: String(OTPReasonCode.WithdrawalSymptoms), labelKey: 'otp.reasonCodes.withdrawalSymptoms' },
    { value: String(OTPReasonCode.MedicalComplication), labelKey: 'otp.reasonCodes.medicalComplication' },
    { value: String(OTPReasonCode.TaperingRateTooFast), labelKey: 'otp.reasonCodes.taperingRateTooFast' },
    { value: String(OTPReasonCode.NonCompliance), labelKey: 'otp.reasonCodes.nonCompliance' },
    { value: String(OTPReasonCode.ClinicalSituationChange), labelKey: 'otp.reasonCodes.clinicalSituationChange' },
];

export const OTPOutcomeDialog: React.FC<IOTPOutcomeDialogProps> = ({
    opened,
    onClose,
    onConfirm,
    action,
    title,
}) => {
    const { t } = useTranslation();
    const [reasonCode, setReasonCode] = useState<string>(String(OTPReasonCode.Other));
    const [reasonText, setReasonText] = useState('');

    const handleClose = () => {
        setReasonCode(String(OTPReasonCode.Other));
        setReasonText('');
        onClose();
    };

    const handleConfirm = () => {
        onConfirm({
            action,
            reasonCode: Number(reasonCode) as OTPReasonCode,
            reasonText: reasonText.trim() || undefined,
        });
        handleClose();
    };

    const requiresReason = action === 'abort' || action === 'append';
    const isValid = !requiresReason || (reasonCode !== undefined);

    const getActionDescription = () => {
        switch (action) {
            case 'complete':
                return t('otp.outcomeDialog.completeDescription');
            case 'abort':
                return t('otp.outcomeDialog.abortDescription');
            case 'append':
                return t('otp.outcomeDialog.appendDescription');
            default:
                return '';
        }
    };

    const getConfirmButtonLabel = () => {
        switch (action) {
            case 'complete':
                return t('otp.outcomeDialog.confirmComplete');
            case 'abort':
                return t('otp.outcomeDialog.confirmAbort');
            case 'append':
                return t('otp.outcomeDialog.confirmAppend');
            default:
                return t('common.confirm');
        }
    };

    const getConfirmButtonColor = () => {
        switch (action) {
            case 'complete':
                return 'blue';
            case 'abort':
                return 'red';
            case 'append':
                return 'orange';
            default:
                return 'blue';
        }
    };

    const getDialogTitle = () => {
        if (title) return title;
        switch (action) {
            case 'complete':
                return t('otp.outcomeDialog.options.complete');
            case 'abort':
                return t('otp.outcomeDialog.options.abort');
            case 'append':
                return t('otp.outcomeDialog.options.append');
            default:
                return t('otp.outcomeDialog.title');
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={getDialogTitle()}
            size="md"
        >
            <Stack gap="md">
                <Text size="sm" c="dimmed">
                    {getActionDescription()}
                </Text>

                {requiresReason && (
                    <Select
                        label={t('otp.outcomeDialog.reasonCode')}
                        placeholder={t('otp.outcomeDialog.selectReason')}
                        value={reasonCode}
                        onChange={(value) => setReasonCode(value || String(OTPReasonCode.Other))}
                        data={reasonCodeOptions.map((opt) => ({
                            value: opt.value,
                            label: t(opt.labelKey),
                        }))}
                        required
                    />
                )}

                <Textarea
                    label={requiresReason ? t('otp.outcomeDialog.additionalNotes') : t('otp.outcomeDialog.completionNotes')}
                    placeholder={t('otp.outcomeDialog.notesPlaceholder')}
                    value={reasonText}
                    onChange={(e) => setReasonText(e.currentTarget.value)}
                    minRows={3}
                />

                <Group justify="flex-end" mt="md">
                    <Button variant="default" onClick={handleClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        color={getConfirmButtonColor()}
                        onClick={handleConfirm}
                        disabled={!isValid}
                    >
                        {getConfirmButtonLabel()}
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};
